import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface TermsConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onClose?: () => void;
  allowClose?: boolean;
}

const TermsConsentModal: React.FC<TermsConsentModalProps> = ({
  isOpen,
  onAccept,
  onClose,
  allowClose = false,
}) => {
  const { t } = useTranslation();
  const [agreed, setAgreed] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (agreed) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('termsConsent.title', 'Review Terms of Service')}
          </h2>
          {allowClose && onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-700">
          {/* SMS Consent */}
          <div className="mb-4">
            <p className="leading-relaxed">
              {t(
                'termsConsent.smsConsent',
                'By checking this box, and clicking "Connect," you agree to receive conversational SMS from ChatNexus. Message frequency may vary. Standard Message and Data Rates may apply. Reply STOP to opt out. Reply HELP for help. Consent is not a condition of purchase.',
              )}
            </p>
          </div>

          <div className="mb-4">
            <p className="leading-relaxed">
              {t(
                'termsConsent.privacyNote',
                'Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.',
              )}
            </p>
          </div>

          {/* Privacy Policy Section */}
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('termsConsent.privacyPolicy', 'Privacy Policy')}
            </h3>
            <p className="leading-relaxed">
              {t(
                'termsConsent.privacyPolicyText',
                'We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you as to how we look after your personal data when you visit our website (regardless of where you visit it from) and tell you about your privacy rights and how the law protects you.',
              )}
            </p>
          </div>

          {/* Data Collection Section */}
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('termsConsent.dataCollection', 'Data Collection')}
            </h3>
            <p className="leading-relaxed mb-3">
              {t(
                'termsConsent.dataCollectionText',
                'We may use, store, and transfer different kinds of personal data about you which we have grouped together as follows:',
              )}
            </p>

            {/* Identity Data */}
            <div className="mb-2">
              <p className="font-medium text-gray-800">
                {t('termsConsent.identityData', 'Identity Data')}
              </p>
              <p className="text-gray-600 text-xs leading-relaxed">
                {t(
                  'termsConsent.identityDataDetails',
                  'includes first name, maiden name, last name, username or similar identifier, marital status, title, date of birth and gender.',
                )}
              </p>
            </div>

            {/* Contact Data */}
            <div className="mb-2">
              <p className="font-medium text-gray-800">
                {t('termsConsent.contactData', 'Contact Data')}
              </p>
              <p className="text-gray-600 text-xs leading-relaxed">
                {t(
                  'termsConsent.contactDataDetails',
                  'includes billing address, delivery address, email address and telephone numbers.',
                )}
              </p>
            </div>
          </div>

          {/* Legal Links */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-600 mb-2">
              {t('termsConsent.viewFullTerms', 'View our complete legal documents:')}
            </p>
            <div className="flex flex-wrap gap-3 text-xs">
              <a
                href="/sms-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 underline"
              >
                {t('termsConsent.smsTermsLink', 'SMS Terms')}
              </a>
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 underline"
              >
                {t('termsConsent.termsLink', 'Terms of Service')}
              </a>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-700 underline"
              >
                {t('termsConsent.privacyLink', 'Privacy Policy')}
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          {/* Checkbox */}
          <div className="flex items-start mb-4">
            <input
              type="checkbox"
              id="terms-consent-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label
              htmlFor="terms-consent-checkbox"
              className="ml-2 text-xs text-gray-700 leading-relaxed"
            >
              {t(
                'termsConsent.agreeCheckbox',
                'By clicking I Agree, I certify I have read and agree to the Terms of Service.',
              )}
            </label>
          </div>

          {/* I Agree Button */}
          <button
            onClick={handleAccept}
            disabled={!agreed}
            className="w-full py-3 px-4 bg-gradient-to-r from-pink-400 to-orange-400 hover:from-pink-500 hover:to-orange-500 disabled:from-gray-300 disabled:to-gray-300 text-white font-medium rounded-xl transition-all disabled:cursor-not-allowed"
          >
            {t('termsConsent.agreeButton', 'I Agree')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsConsentModal;
